import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosInstance';
import { useAuth } from './AuthContext';

export interface Company {
  id: string;
  name: string;
  code?: string;
  isActive: boolean;
  tenantId?: string;
  logo?: string;
}

interface CompanyContextType {
  companies: Company[];
  selectedCompany: Company | null;
  loading: boolean;
  error: string | null;
  selectCompany: (company: Company) => void;
  refreshCompanies: () => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: React.ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated } = useAuth();

  // Firma seçme fonksiyonu
  const selectCompany = useCallback((company: Company) => {
    setSelectedCompany(company);
    localStorage.setItem('qubepro_selected_company_id', company.id);

    // Global event dispatch - tüm modüller dinleyebilir
    window.dispatchEvent(new CustomEvent('companyChanged', {
      detail: { company }
    }));
  }, []);

  // Firmaları yükleme fonksiyonu
  const refreshCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/auth/me/companies');

      // API'den gelen veriyi Company interface'ine uygun hale getir
      const responseData = response.data?.data || response.data || [];
      const companiesData = Array.isArray(responseData) ? responseData : [];

      const formattedCompanies = companiesData.map((company: any) => {
        // Handle translations locally if available
        const currentLang = localStorage.getItem('qubepro_language') || 'tr';
        const translatedName = company._translations?.name?.[currentLang] || company.name || company.companyName;

        return {
          id: company.id?.toString() || company.companyId?.toString(),
          name: translatedName,
          code: company.code || company.companyCode,
          isActive: company.isActive !== false,
          tenantId: company.tenantId?.toString() || company.id?.toString(),
          logo: company.logo
        };
      });

      setCompanies(formattedCompanies);

      // Önceden seçilmiş firma varsa geri yükle
      const savedCompanyId = localStorage.getItem('qubepro_selected_company_id') || localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && formattedCompanies.length > 0) {
        const savedCompany = formattedCompanies.find((c: Company) => c.id === savedCompanyId);
        if (savedCompany) {
          setSelectedCompany(savedCompany);
        } else {
          setSelectedCompany(formattedCompanies[0]);
          localStorage.setItem('qubepro_selected_company_id', formattedCompanies[0].id);
        }
        if (localStorage.getItem('selectedCompanyId')) localStorage.removeItem('selectedCompanyId');
      } else if (formattedCompanies.length > 0) {
        setSelectedCompany(formattedCompanies[0]);
        localStorage.setItem('qubepro_selected_company_id', formattedCompanies[0].id);
      }
    } catch (err: any) {
      console.error('Firmalar yüklenirken hata:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Firmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  // İlk yükleme veya yetki durumu değişimi
  useEffect(() => {
    if (isAuthenticated) {
      refreshCompanies();
    } else {
      setCompanies([]);
      setSelectedCompany(null);
    }
  }, [refreshCompanies, isAuthenticated]);

  const value: CompanyContextType = {
    companies,
    selectedCompany,
    loading,
    error,
    selectCompany,
    refreshCompanies
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}